import React, { useCallback, useContext, useState } from 'react';
import FormDefault from '~/components/FormDefault';
import { Container } from './styles';
import { loadingContext } from '~/context/loading';
import { Col, Row } from 'react-bootstrap';
import { useAgendContas } from './hook/useAgendContas';
import { GridRowParams } from '@material-ui/data-grid';
import Search from '~/components/Search';
import DefaultLoader from '~/components/DefaultLoader';
import { Loja } from '~/components/NovosInputs';
import { Fields } from './components/fields';
import { Observacao } from './components/observacao';
import { toast } from 'react-toastify';
import { formatCurrencyAsText, transformAsCurrency } from '~/utils/functions';
import {
  tipoDtaEntrada,
  tiposFrequencia,
  agendamentoDataProps,
  formData,
} from './types';
import { agendamentoServices } from './services';
import moment from 'moment';

const AgendContasContent: React.FC = () => {
  const [showSearch, setShowSearch] = useState(true);
  const { setLoading, loading } = useContext(loadingContext);
  const {
    formAgendamento,
    setIsUpdate,
    isUpdate,
    handleChangeTipoConta,
    tipoConta,
    setContaBancoRetriever,
    setLojaRetriever,
    loja,
    handleDisableQtdParcelas,
    handleDisabledDtaTermino,
  } = useAgendContas();
  const { setValue, register, handleSubmit, control, reset, getValues, watch } =
    formAgendamento;
  const { createAgendamento, updateAgendamento, deleteAgendamento } =
    agendamentoServices;

  const onRowClick = useCallback(
    async (param: GridRowParams) => {
      const { row } = param;
      setIsUpdate(true);
      const {
        cod_categoria,
        descricao,
        cod_cc,
        cod_pessoa,
        nome_pessoa,
        des_cidade,
        des_uf,
        cod_finalizadora,
        des_finalizadora,
        cod_loja,
        des_loja,
        des_loja_cidade,
        cod_seq_agendamento,
        des_observacao,
        tipo_frequencia,
        dta_docto,
        dta_termino,
        num_dia_entrada,
        qtd_parcelas,
        tipo_conta,
        tipo_dta_entrada,
        val_docto,
      } = row;
      const tipoDtaLabel = tipoDtaEntrada.find(
        (item) => item.value === tipo_dta_entrada,
      );
      const tipoFreqLabel = tiposFrequencia.find(
        (item) => item.value === tipo_frequencia,
      );
      reset({
        cod_seq_agendamento,
        num_dia_entrada,
        qtd_parcelas,
        des_observacao,
        cod_loja: {
          value: cod_loja,
          label: `${des_loja} (${des_loja_cidade})`,
        },
        dta_docto: moment(dta_docto).format('YYYY-MM-DD'),
        dta_termino: moment(dta_termino).format('YYYY-MM-DD'),
        val_docto: formatCurrencyAsText(val_docto),
        cod_categoria: { value: cod_categoria, label: descricao },
        cod_finalizadora: {
          value: cod_finalizadora,
          label: `${cod_finalizadora} - ${des_finalizadora}`,
        },
        cod_pessoa: {
          value: cod_pessoa,
          label: `${cod_pessoa} - ${nome_pessoa} ${
            des_cidade === null || des_uf === null
              ? ''
              : ` (${des_cidade} - ${des_uf})`
          }`,
        },
        tipo_frequencia: {
          value: tipo_frequencia,
          label: tipoFreqLabel?.label,
        },
        tipo_dta_entrada: {
          value: tipo_dta_entrada,
          label: tipoDtaLabel?.label,
        },
      });
      Number(qtd_parcelas) === 0
        ? handleDisabledDtaTermino(true)
        : handleDisabledDtaTermino(false);

      tipo_conta === 1
        ? handleChangeTipoConta({ value: 1, label: 'pgto' })
        : handleChangeTipoConta({ value: 0, label: 'receb' });
      setLojaRetriever(cod_loja);
      setContaBancoRetriever(cod_cc);
      tipo_frequencia === 0
        ? handleDisableQtdParcelas(true)
        : handleDisableQtdParcelas(false);
      setLoading(false);
      setShowSearch(false);
    },
    [
      reset,
      setIsUpdate,
      handleChangeTipoConta,
      handleDisabledDtaTermino,
      handleDisableQtdParcelas,
      setContaBancoRetriever,
      setLojaRetriever,
      setLoading,
    ],
  );

  const onSave = handleSubmit(async (dataForm) => {
    setLoading(true);
    try {
      const insertValues: formData = {
        cod_categoria: dataForm.cod_categoria.value,
        cod_cc: dataForm.cod_cc.value,
        cod_loja: dataForm.cod_loja.value,
        cod_finalizadora: dataForm.cod_finalizadora.value,
        cod_pessoa: dataForm.cod_pessoa.value,
        tipo_frequencia: dataForm.tipo_frequencia.value,
        tipo_dta_entrada: dataForm.tipo_dta_entrada.value,
        num_dia_entrada:
          Number(dataForm.num_dia_entrada) < 1
            ? 1
            : Number(dataForm.num_dia_entrada),
        qtd_parcelas: Number(dataForm.qtd_parcelas),
        val_docto: transformAsCurrency(dataForm.val_docto),
        dta_docto: dataForm.dta_docto,
        dta_termino: dataForm.dta_termino === '' ? null : dataForm.dta_termino,
        des_observacao: dataForm.des_observacao,
        tipo_conta: tipoConta?.value,
      };
      const agendamentoData: agendamentoDataProps = {
        insertValues,
      };
      if (isUpdate) {
        const { message, success } = await updateAgendamento(
          watch('cod_seq_agendamento'),
          agendamentoData,
        );
        if (success) {
          resetFormData();
          setShowSearch(true);
          return toast.success(message);
        }
      }
      const { message, success } = await createAgendamento(agendamentoData);
      if (success) {
        resetFormData();
        setShowSearch(false);
        toast.success(message);
      }
    } catch (e: any) {
      toast.warn(e.message);
    } finally {
      setLoading(false);
    }
  });

  const resetFormData = useCallback(() => {
    reset({
      cod_loja: {
        value: loja.cod_loja,
        label: `${loja.des_loja} (${loja.des_cidade})`,
      },
      dta_docto: '',
      dta_termino: '',
      val_docto: '',
      num_dia_entrada: 1,
      qtd_parcelas: 1,
      cod_pessoa: '',
      cod_cc: { value: undefined, label: undefined },
      cod_finalizadora: '',
      cod_categoria: '',
      tipo_dta_entrada: '',
      tipo_frequencia: '',
      des_observacao: '',
    });
    setContaBancoRetriever(null);
    handleDisableQtdParcelas(false);
    handleDisabledDtaTermino(false);
    handleChangeTipoConta({ value: 0, label: 'pgto' });
  }, [
    handleChangeTipoConta,
    reset,
    loja,
    setContaBancoRetriever,
    handleDisableQtdParcelas,
    handleDisabledDtaTermino,
  ]);

  const handleDelete = async () => {
    try {
      const { message, success } = await deleteAgendamento(
        watch('cod_seq_agendamento'),
      );
      if (success) {
        resetFormData();
        setIsUpdate(false);
        setShowSearch(true);
        toast.success(message);
      }
    } catch (e: any) {
      toast.warn(e.message);
    }
  };

  const newData = () => {
    resetFormData();
    setShowSearch(false);
    setIsUpdate(false);
  };

  if (loading) {
    return <DefaultLoader />;
  }
  return (
    <Container>
      {showSearch && (
        <Search newData={newData} onRowClick={onRowClick} codTela={314} />
      )}
      {!showSearch && (
        <FormDefault
          codTela={314}
          title="Agendamento de Contas"
          codigoRegistro={[
            {
              value: watch('cod_seq_agendamento'),
              des_campo: 'Código',
            },
          ]}
          onSave={async () => {
            await onSave();
          }}
          isUpdate={isUpdate}
          onCancel={() => setShowSearch(true)}
          onNew={newData}
          onDelete={handleDelete}
          onClearFields={resetFormData}
          onReturnSearch={() => setShowSearch(true)}
        >
          <Row>
            <Col md={12} sm={12}>
              <Loja
                label="Loja"
                name="cod_loja"
                placeholder="Selecione..."
                register={register}
                control={control}
                isError={false}
                getValues={getValues}
                disabled={isUpdate}
                changeSelected={(ev) => {
                  setValue('cod_loja', ev);
                }}
              />
            </Col>
          </Row>
          <Row>
            <Fields />
          </Row>
          <Row>
            <Observacao />
          </Row>
        </FormDefault>
      )}
    </Container>
  );
};

export default AgendContasContent;
